<template>
  <div class="chart-wrapper" :style="{ height: `${height}px` }">
    <canvas ref="chartRef"></canvas>
  </div>
</template>

<script setup lang="ts">
import Chart from "@/chart-setup";
import { StackableDataset } from "@/types/ChartData";
import { type ChartConfiguration } from "chart.js";
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  useTemplateRef,
  watch,
} from "vue";

const props = withDefaults(
  defineProps<{
    chartData: {
      labels: string[];
      datasets: StackableDataset[];
    };
    title?: string;
    subtitle?: string;
    yAxisTitle?: string;
    height?: number;
    showLegend?: boolean;
  }>(),
  {
    title: "",
    subtitle: "",
    yAxisTitle: "MWh",
    height: 190,
    showLegend: true,
  }
);

// Chart.js draws each tooltip line as one unbroken string, so a sentence has to be broken up here.
const DESCRIPTION_LINE_LENGTH = 48;

const chartRef = useTemplateRef<HTMLCanvasElement>("chartRef");
let chart: Chart<any> | null = null;

/** A dataset's `description` as tooltip lines of at most about `DESCRIPTION_LINE_LENGTH` characters. */
const descriptionLines = (description: string | undefined): string[] =>
  (description ?? "")
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .reduce<string[]>((lines, word) => {
      const last = lines.at(-1);
      if (
        last !== undefined &&
        last.length + word.length < DESCRIPTION_LINE_LENGTH
      ) {
        return [...lines.slice(0, -1), `${last} ${word}`];
      }
      return [...lines, word];
    }, []);

const createChart = () => {
  if (chartRef.value) {
    const ctx = chartRef.value.getContext("2d");
    if (ctx) {
      const config: ChartConfiguration = {
        type: "bar",
        data: props.chartData,
        options: {
          plugins: {
            legend: {
              display: props.showLegend,
              position: "top",
            },
            title: {
              display: true,
              text: props.title,
              font: {
                size: 16,
                family: "Nunito Sans",
              },
              padding: {
                top: 5,
                bottom: 0,
              },
            },
            subtitle: {
              display: !!props.subtitle,
              text: props.subtitle,
              font: {
                size: 14,
                style: "italic",
                family: "Nunito Sans",
              },
              padding: {
                top: 0,
                bottom: 0,
              },
            },
            tooltip: {
              callbacks: {
                label: function (context: any) {
                  let label = context.dataset.label || "";
                  if (label) {
                    label += ": ";
                  }
                  if (context.parsed.y !== null) {
                    label +=
                      Math.round(context.parsed.y) + " " + props.yAxisTitle;
                  }
                  return label;
                },
                afterLabel: (context: any) =>
                  descriptionLines(context.dataset.description),
              },
            },
          },
          scales: {
            x: { stacked: true },
            y: {
              stacked: true,
              beginAtZero: true,
              title: { display: true, text: props.yAxisTitle },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
        },
      };

      chart = new Chart(ctx, config);
    }
  }
};

const updateTitleAndSubtitle = (chart: Chart) => {
  if (chart?.options?.plugins?.title) {
    chart.options.plugins.title.text = props.title;
  }
  if (chart?.options?.plugins?.subtitle) {
    chart.options.plugins.subtitle.text = props.subtitle;
    chart.options.plugins.subtitle.display = !!props.subtitle;
  }
};

const updateChart = () => {
  if (chart) {
    chart.data = props.chartData;
    updateTitleAndSubtitle(chart);
    if (chart.options?.plugins?.legend) {
      chart.options.plugins.legend.display = props.showLegend;
    }
    chart.update();
  }
};

onMounted(async () => {
  await nextTick();
  createChart();
});

onBeforeUnmount(() => {
  if (chart) {
    chart.destroy();
    chart = null;
  }
});

watch(() => props.chartData, updateChart, { deep: true });
watch(() => props.title, updateChart);
watch(() => props.subtitle, updateChart);
watch(() => props.showLegend, updateChart);
watch(
  () => props.height,
  async () => {
    await nextTick();
    if (chart) {
      chart.resize();
    }
  }
);
</script>

<style scoped lang="scss">
.chart-wrapper {
  position: relative;
  margin-bottom: 15px;
}
</style>
